/**
 * @file intervals-sync.ts
 * @description Sync logic for intervals.icu integration.
 * 
 * ============================================================================
 * ⚠️  CRITICAL DATA BOUNDARY DOCUMENTATION
 * ============================================================================
 * 
 * This module handles 2-way synchronization with intervals.icu, but is
 * STRICTLY LIMITED to the following data:
 * 
 * ✅ ALLOWED TO SYNC:
 *    - Training zones (power zones, heart rate zones, pace zones)
 *    - Threshold metrics (FTP, LTHR, threshold pace, CSS, max HR)
 *    - Profile picture (read-only from intervals.icu)
 *    - Athlete name (read-only, for display purposes)
 * 
 * ❌ NEVER SYNCED (by design):
 *    - Training plans
 *    - Schedule/blocks/ATP structure
 *    - Availability settings
 *    - App preferences/settings
 *    - Goal/event configuration
 *    - API keys or credentials
 * 
 * The `SyncResult` interface enforces this boundary - it only contains
 * `metrics` and `zones`, nothing else.
 * 
 * See DATA-STORAGE.md for the complete data storage architecture.
 * ============================================================================
 */

import { format } from 'date-fns';
import { intervalsClient, type IntervalsAthlete, type SportSetting } from './intervals-client';
import type { IntervalsActivity, ZoneSet } from '@/types';
import { createLogger } from '../logger';
import type { DateBoundedFetchOptions, UnifiedActivity } from './activity-sync-schema';

const log = createLogger('intervals-sync');

export interface SyncResult {
    metrics: {
        ftp: number;
        lthr: number; // Primary (Run) fallback
        cyclingLthr: number;
        runningLthr: number;
        swimmingLthr: number;
        thresholdPace: number;
        swimCss: number;
        maxHr?: number;
    };
    zones: {
        running: ZoneSet;
        cycling: ZoneSet;
        swimming: ZoneSet;
    };
}

const ZONE_NAMES = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7'];
const COLORS = [
    'hsl(var(--zone-1, 200, 70%, 60%))',
    'hsl(var(--zone-2, 142, 76%, 36%))',
    'hsl(var(--zone-3, 45, 93%, 47%))',
    'hsl(var(--zone-4, 27, 96%, 61%))',
    'hsl(var(--zone-5, 0, 84%, 60%))',
    'hsl(var(--zone-6, 0, 84%, 40%))',
    'hsl(var(--zone-7, 0, 84%, 20%))'
];

export function syncIntervalsData(
    data: IntervalsAthlete,
    // Provide defaults if missing in API
    currentMetrics: {
        ftp: number;
        lthr: number;
        thresholdPace: number;
        swimCss?: number;
        swimLthr?: number;
    }
): SyncResult {
    // 1. Find Sport Settings
    const cyclingSetting = data.sport_settings?.find(s =>
        s.types?.some(t => t.toLowerCase().includes('ride') || t.toLowerCase().includes('cycling'))
    );

    const runningSettings = data.sport_settings?.filter(s =>
        s.types?.some(t => t.toLowerCase().includes('run'))
    ) || [];
    const runningSetting = runningSettings.find(s => s.threshold && s.threshold > 0) || runningSettings[0];

    const swimmingSetting = data.sport_settings?.find(s =>
        s.types?.some(t => t.toLowerCase().includes('swim'))
    );

    // 2. Sync Base Metrics
    const newCycleFtp = cyclingSetting?.ftp || data.ftp || currentMetrics.ftp;
    const newCycleLthr = cyclingSetting?.lthr || data.icu_lthr || data.lthr || currentMetrics.lthr;
    const newRunLthr = runningSetting?.lthr || data.icu_lthr || data.lthr || currentMetrics.lthr;
    const newSwimLthr = swimmingSetting?.lthr || data.icu_lthr || data.lthr || currentMetrics.swimLthr || currentMetrics.lthr;

    // Threshold Pace Selection
    // intervals.icu has two threshold fields:
    // - threshold: speed in m/s (needs conversion to pace)
    // - threshold_pace: also speed in m/s (same as threshold for running)
    const sportThresholdSpeed = runningSetting?.threshold || runningSetting?.threshold_pace;
    const globalThresholdSpeed = data.lthr_pace || data.icu_lthr_pace;

    const normalizePace = (val?: number): number | null => {
        if (!val || val <= 0) return null;
        return 1000 / val; // m/s to s/km (pace)
    };

    const sportPace = normalizePace(sportThresholdSpeed);
    const globalPace = normalizePace(globalThresholdSpeed);
    const newRunThresholdPace = (sportPace || globalPace || currentMetrics.thresholdPace);

    log.debug('syncData', 'Threshold extraction', {
        sportThresholdSpeed,
        globalThresholdSpeed,
        sportPace,
        globalPace,
        final: newRunThresholdPace
    });

    // Swimming CSS: Intervals uses m/s, we use s/100m
    const swimThreshold = swimmingSetting?.threshold;
    const newSwimCss = swimThreshold ? (100 / swimThreshold) : (currentMetrics.swimCss || 100);

    // Max HR
    const remoteMaxHr = runningSetting?.max_hr || cyclingSetting?.max_hr || data.max_hr;

    // 3. Map Zones (with global fallback)
    const runningZones = mapZones(runningSetting, data);
    const cyclingZones = mapZones(cyclingSetting, data);
    const swimmingZones = mapZones(swimmingSetting, data);

    return {
        metrics: {
            ftp: newCycleFtp,
            lthr: newRunLthr,
            cyclingLthr: newCycleLthr,
            runningLthr: newRunLthr,
            swimmingLthr: newSwimLthr,
            thresholdPace: Math.round(newRunThresholdPace),
            swimCss: Math.round(newSwimCss),
            maxHr: remoteMaxHr,
        },
        zones: {
            running: runningZones,
            cycling: cyclingZones,
            swimming: swimmingZones,
        }
    };
}

/**
 * Map zones from Intervals.icu sport settings format to our ZoneSet format.
 * Intervals.icu uses:
 * - power_zones: number[] (% of FTP thresholds, e.g. [55, 75, 90, 105, 120, 150, 999])
 * - power_zone_names: string[]
 * - hr_zones: number[] (BPM values at zone boundaries)
 * - hr_zone_names: string[]
 * - pace_zones: number[] (% of threshold pace)
 * - pace_zone_names: string[]
 * 
 * FALLBACKS:
 * If a sport-specific setting is missing a zone type, we fall back to the global
 * athlete settings (data.power_zones, etc.).
 */
function mapZones(setting: SportSetting | undefined, globalData: IntervalsAthlete): ZoneSet {
    const isSwimming = setting?.types?.some((t) => t.toLowerCase().includes('swim')) ?? false;

    // Helper: extract flat array from either sport setting or global data
    const getZoneValues = (key: 'power_zones' | 'hr_zones' | 'pace_zones'): number[] | undefined => {
        const sportVal = setting?.[key] as number[] | undefined;
        if (sportVal && sportVal.length > 0) return sportVal;
        return globalData[key as keyof IntervalsAthlete] as number[] | undefined;
    };

    const getZoneNames = (key: 'power_zone_names' | 'hr_zone_names' | 'pace_zone_names'): string[] | undefined => {
        const sportVal = setting?.[key] as string[] | undefined;
        if (sportVal && sportVal.length > 0) return sportVal;
        return globalData[key as keyof IntervalsAthlete] as string[] | undefined;
    };

    // Power zones
    const powerZoneValues = getZoneValues('power_zones');
    const powerZoneNames = getZoneNames('power_zone_names');
    const powerZones = powerZoneValues?.map((maxPct, i) => ({
        name: powerZoneNames?.[i] || ZONE_NAMES[i] || `Zone ${i + 1}`,
        minPercent: i === 0 ? 0 : powerZoneValues[i - 1],
        maxPercent: maxPct,
        description: powerZoneNames?.[i] || `Zone ${i + 1}`,
        color: COLORS[i] || COLORS[COLORS.length - 1]
    })) || [];

    // HR zones
    const hrZoneValues = getZoneValues('hr_zones');
    const hrZoneNames = getZoneNames('hr_zone_names');
    const lthr = setting?.lthr || globalData.icu_lthr || globalData.lthr;
    const hrZones = hrZoneValues?.map((maxBpm, i) => {
        const minBpm = i === 0 ? 0 : hrZoneValues[i - 1];
        const minPct = lthr ? Math.round((minBpm / lthr) * 100) : 0;
        const maxPct = lthr ? Math.round((maxBpm / lthr) * 100) : 100;
        return {
            name: hrZoneNames?.[i] || ZONE_NAMES[i] || `Zone ${i + 1}`,
            minPercent: minPct,
            maxPercent: maxPct,
            description: hrZoneNames?.[i] || `Zone ${i + 1}`,
            color: COLORS[i] || COLORS[COLORS.length - 1]
        };
    }) || [];

    // Pace zones
    const paceZoneValues = getZoneValues('pace_zones');
    const paceZoneNames = getZoneNames('pace_zone_names');
    const mappedPaces = paceZoneValues?.map((maxPct, i) => ({
        name: paceZoneNames?.[i] || ZONE_NAMES[i] || `Zone ${i + 1}`,
        minPercent: i === 0 ? 0 : paceZoneValues[i - 1],
        maxPercent: maxPct,
        description: paceZoneNames?.[i] || `Zone ${i + 1}`,
        color: COLORS[i] || COLORS[COLORS.length - 1]
    })) || [];

    return {
        power: powerZones,
        heartRate: hrZones,
        pace: isSwimming ? [] : mappedPaces,
        swimPace: isSwimming ? mappedPaces : []
    };
}

/**
 * Format local zones into Intervals.icu SportSetting payload.
 * Converts from our ZoneSet format to the flat arrays Intervals expects.
 */
export function prepareIntervalsPayload(
    zones: ZoneSet,
    metrics: { ftp?: number; lthr?: number; thresholdPace?: number; maxHr?: number }
): Partial<SportSetting> {
    log.debug('preparePayload', 'Input zones', {
        power: zones.power?.length,
        hr: zones.heartRate?.length,
        pace: zones.pace?.length,
    });
    log.debug('preparePayload', 'Input metrics', metrics as Record<string, unknown>);

    const payload: Partial<SportSetting> = {};

    // 1. Sync Scalars
    if (metrics.ftp) payload.ftp = metrics.ftp;
    if (metrics.lthr) payload.lthr = metrics.lthr;
    if (metrics.maxHr) payload.max_hr = metrics.maxHr;

    // Threshold Pace: Local is sec/km (pace), Intervals is m/s (speed)
    if (metrics.thresholdPace) {
        // pace = 1000 / speed  => speed = 1000 / pace
        const speedMs = 1000 / metrics.thresholdPace;
        payload.threshold = speedMs;
        payload.threshold_pace = speedMs;
    }

    // 2. Format Power Zones (Percent of FTP)
    if (zones.power && zones.power.length > 0) {
        const values = zones.power.map(z => z.maxPercent);
        // Force last zone to 'infinite'
        values[values.length - 1] = 999;

        // Ensure strictly increasing
        for (let i = 1; i < values.length; i++) {
            if (values[i] <= values[i - 1]) values[i] = values[i - 1] + 1;
        }

        payload.power_zones = values;
        payload.power_zone_names = zones.power.map(z => z.name);
    }

    // 3. Format HR Zones (BPM)
    if (zones.heartRate && zones.heartRate.length > 0 && metrics.lthr) {
        const hrValues = zones.heartRate.map(z => Math.round((z.maxPercent / 100) * metrics.lthr!));

        // Final zone is Max HR or 100% of LTHR + buffer if no maxHr provided
        if (metrics.maxHr) {
            hrValues[hrValues.length - 1] = metrics.maxHr;
        } else {
            // If no maxHr, ensure it's at least higher than previous
            if (hrValues[hrValues.length - 1] <= hrValues[hrValues.length - 2]) {
                hrValues[hrValues.length - 1] = hrValues[hrValues.length - 2] + 5;
            }
        }

        // Ensure strictly increasing (mandatory for Intervals.icu)
        for (let i = 1; i < hrValues.length; i++) {
            if (hrValues[i] <= hrValues[i - 1]) hrValues[i] = hrValues[i - 1] + 1;
        }

        // CRITICAL FIX: Ensure HR zones never exceed Max HR if we have it
        // If they do, cap them and adjust predecessors to maintain increasing order
        if (metrics.maxHr) {
            hrValues[hrValues.length - 1] = metrics.maxHr;
            for (let i = hrValues.length - 2; i >= 0; i--) {
                if (hrValues[i] >= hrValues[i + 1]) {
                    hrValues[i] = hrValues[i + 1] - 1;
                }
            }
        }

        payload.hr_zones = hrValues;
        payload.hr_zone_names = zones.heartRate.map(z => z.name);
    }

    // 4. Format Pace Zones (Percent of Threshold Speed)
    if (zones.pace && zones.pace.length > 0) {
        const pValues = zones.pace.map(z => z.maxPercent);
        // Force last zone to 'infinite'
        pValues[pValues.length - 1] = 999;

        // Ensure strictly increasing
        for (let i = 1; i < pValues.length; i++) {
            if (pValues[i] <= pValues[i - 1]) pValues[i] = pValues[i - 1] + 1;
        }

        payload.pace_zones = pValues;
        payload.pace_zone_names = zones.pace.map(z => z.name);
    }

    log.debug('preparePayload', 'Final payload prepared', { keys: Object.keys(payload) });
    return payload;
}

export function mapIntervalsActivityToUnified(
    activity: IntervalsActivity,
    athleteId: string,
    syncedAt: string
): UnifiedActivity {
    const parsedStartDate = new Date(activity.start_date_local);
    const startDateIso = Number.isNaN(parsedStartDate.getTime())
        ? syncedAt
        : parsedStartDate.toISOString();

    return {
        athlete_id: athleteId,
        external_id: activity.id,
        source: 'intervals',
        type: activity.type,
        name: activity.name,
        start_date: startDateIso,
        start_date_local: activity.start_date_local,
        timezone: 'UTC',
        distance: activity.distance ?? null,
        moving_time: activity.moving_time ?? null,
        elapsed_time: activity.elapsed_time ?? null,
        total_elevation_gain: activity.total_elevation_gain ?? null,
        average_heartrate: activity.average_heartrate ?? null,
        max_heartrate: activity.max_heartrate ?? null,
        average_speed: activity.average_speed ?? null,
        raw_data: activity as unknown as Record<string, unknown>,
        synced_at: syncedAt,
    };
}

export async function getUnifiedIntervalsActivitiesInDateRange(
    athleteId: string,
    options: DateBoundedFetchOptions = {}
): Promise<UnifiedActivity[]> {
    const newest = format(options.before ?? new Date(), 'yyyy-MM-dd');
    const oldest = format(options.after ?? new Date(0), 'yyyy-MM-dd');
    const syncedAt = new Date().toISOString();
    const activities = await intervalsClient.getActivities(oldest, newest);
    return activities.map((activity) => mapIntervalsActivityToUnified(activity, athleteId, syncedAt));
}
